import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Checkbox, Drawer, FormControlLabel, FormGroup, IconButton, Typography } from '@mui/material';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator } from '@mui/icons-material';
import Button from '@mui/material/Button';

export default function ColumnsConfigureDrawer({ open, onClose, onConfirm, columns = [], onReset }) {
  const [tempColumnVisibility, setTempColumnVisibility] = useState({});
  const [tempColumnOrder, setTempColumnOrder] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setTempColumnOrder((_columnOrder) => {
        const oldIndex = _columnOrder.indexOf(active.id);
        const newIndex = _columnOrder.indexOf(over.id);
        return arrayMove(_columnOrder, oldIndex, newIndex);
      });
    }
  }

  const handleToggleVisibility = (key) => {
    setTempColumnVisibility((prev) => ({
      ...prev,
      [key]: !(prev[key] || prev[key] === undefined),
    }));
  };

  const onApply = () => {
    onConfirm(
      tempColumnOrder.map((key) => {
        const column = columns.find((column) => column.key === key);
        return { ...column, hidden: !tempColumnVisibility[key] };
      }),
    );
    onClose && onClose();
  };

  useEffect(() => {
    if (open) {
      setTempColumnVisibility(Object.fromEntries(columns.map((column) => [column.key, !column.hidden])));
      setTempColumnOrder(columns.map((column) => column.key));
    }
  }, [open, columns]); // ✅ Added 'columns' as a dependency to fix the warning

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <Typography variant='h6' borderBottom={1} borderColor='grey.300' p={2}>
          Configure
        </Typography>
        <Typography p={2}>Select columns to display in table</Typography>
        <Box display='flex' flexDirection='column' gap={1} flexGrow={1} height={0} overflow='auto' p={2}>
          <SortableContext items={tempColumnOrder}>
            {tempColumnOrder.map((key) => {
              const col = columns.find((col) => col.key === key);
              if (!col) return null;

              return (
                <DraggableHeader
                  key={key}
                  col={col}
                  checked={tempColumnVisibility[key] !== false}
                  onChange={handleToggleVisibility.bind(null, key)}
                />
              );
            })}
          </SortableContext>
        </Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          gap={1}
          borderTop={1}
          borderColor='grey.300'
          p={2}
        >
          <Button variant='contained' color='primary' onClick={onApply}>
            Save as preference
          </Button>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => {
              onClose();
              onReset();
            }}
          >
            Reset as default
          </Button>
        </Box>
      </DndContext>
    </Drawer>
  );
}

const DraggableHeader = ({ col, checked, onChange }) => {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: col.key,
  });
  const style = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition,
    whiteSpace: 'nowrap',
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} key={col.key}>
      <Box display='flex' alignItems='center' gap={1}>
        <IconButton {...attributes} {...listeners}>
          <DragIndicator />
        </IconButton>
        <FormGroup>
          <FormControlLabel control={<Checkbox checked={checked} onChange={onChange} />} label={col.label} />
        </FormGroup>
      </Box>
    </div>
  );
};
